import { Link } from 'react-router-dom';
import { type QuickStartRealState, resolveQuickStartSteps } from '../lib/quickStartGuide';
import { Card } from './Card';
import styles from './QuickStartGuide.module.css';

interface QuickStartGuideProps {
  state: QuickStartRealState;
  onDismiss: () => void;
}

/**
 * LANE U1, piece (b) — Jason's direct spec: "a welcome to Mira... a quick
 * start guide that CAN BE DISMISSED." Shown on Home for a signed-in user who
 * hasn't dismissed it (Home.tsx owns the visibility check via
 * useQuickStartGuide/shouldShowQuickStart — this component always renders
 * its content once mounted, the caller decides WHETHER to mount it).
 *
 * Every step's done-check comes from lib/quickStartGuide.ts's
 * resolveQuickStartSteps — real served state in, boolean out, never a
 * fabricated checkmark (NO INVENTION). This component itself holds zero
 * done-derivation logic; it only renders what that pure function already
 * decided, so the guard tests on that module are the single source of truth
 * for "when is a checkmark honest."
 *
 * Dismiss (the X) persists via useQuickStartGuide's localStorage-backed
 * writeQuickStartDismissed — nothing unrecoverable: Settings.tsx carries a
 * "Show the quick start again" re-entry that clears the same flag.
 */
export function QuickStartGuide({ state, onDismiss }: QuickStartGuideProps) {
  const steps = resolveQuickStartSteps(state);
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <div>
          <div className={styles.kicker}>Quick start</div>
          <div className={styles.title}>Here's how this works</div>
        </div>
        <button
          type="button"
          className={styles.dismissButton}
          onClick={onDismiss}
          aria-label="Dismiss the quick start guide"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className={styles.progressNote}>
        {doneCount} of {steps.length} done
      </div>

      <div className={styles.steps}>
        {steps.map((step, index) => (
          <Link key={step.id} to={step.path} className={styles.step} data-done={step.done}>
            <div className={styles.stepMarker}>
              {step.done ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <span className={styles.stepNumber}>{index + 1}</span>
              )}
            </div>
            <div className={styles.stepBody}>
              <div className={styles.stepTitle}>{step.title}</div>
              <div className={styles.stepText}>{step.body}</div>
            </div>
            <svg width="7" height="13" viewBox="0 0 9 16" fill="none" aria-hidden="true" className={styles.stepChevron}>
              <path
                d="M1 1l6 7-6 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        ))}
      </div>
    </Card>
  );
}
